import { useRoutes, useLocation, Navigate } from "react-router-dom";
import {
  AdminLayout,
  HRLayout,
  EmployeeLayout,
  NonAuthLayout,
  SuperAdminLayout,
  PMOLayout,
  CRMLayout,
  AccountSetupLayout,
} from "../components/Layouts/PageLayout";
import { PUBLIC_ROUTES, getRoutesForRole, AppRoute } from "./routes";
import {
  selectRoles,
  selectIsAuthenticated,
  selectAuthUser,
  selectShowWelcome,
} from "../store/slices/auth/auth.selector";
import { RootState, useAppSelector } from "../store";
import { UserRole } from "../types/users";
import FullPageLoader from "components/Loaders/FullPageLoader";
import Unauthorized from "pages/Authentication/Unauthorized";
import { useEffect, useState } from "react";
import OnboardingSuccess from "pages/AccountSetup/OrganizationOnboarding/OnboardingSuccess";
import { getDefaultRouteByRole } from "./helpers";
import { NotificationSSEProvider } from "../context/NotificationSSEProvider";

// ─── Layout map keyed by path prefix ────────────────────────────────────────
const PATH_PREFIX_LAYOUT_MAP: Array<{ prefix: string; layout: React.ComponentType<{ children: React.ReactNode }> }> = [
  { prefix: "/pmo",  layout: PMOLayout },
  { prefix: "/hrms", layout: HRLayout },
  { prefix: "/crm",  layout: CRMLayout },
  { prefix: "/settings", layout: AdminLayout },
];

// ─── Role → default layout (fallback when no prefix matches) ─────────────────
const ROLE_DEFAULT_LAYOUT: Record<UserRole, React.ComponentType<{ children: React.ReactNode }>> = {
  ORG_OWNER:    SuperAdminLayout,
  ORG_ADMIN:    AdminLayout,
  HRM_ADMIN:    HRLayout,
  HRM_MANAGER:  HRLayout,
  HRM_EMPLOYEE: HRLayout,
  PMO_ADMIN:    PMOLayout,
  PMO_MANAGER:  PMOLayout,
  PMO_USER:     PMOLayout,
  CRM_ADMIN:    CRMLayout,
  CRM_MANAGER:  CRMLayout,
  CRM_USER:     CRMLayout,
  EMPLOYEE:     EmployeeLayout,
  NONE:         EmployeeLayout,
};

const SYSTEM_SLUGS = [
  "login", "signup", "unauthorized", "invite", "reset-password",
  "forgot-password", "verify-otp", "organization-account-setup",
  "invited-signup", "accept-invite", "invite-already-accepted",
  "join-invited-user",
];

const PUBLIC_PATHS_LIST = [
  "/login", "/signup", "/forgot-password", "/verify-otp",
  "/organization-account-setup", "/invited-signup", "/accept-invite",
  "/invite-already-accepted", "/join-invited-user", "/reset-password", "/unauthorized",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const resolveRoutesByRoles = (roles: UserRole[]): AppRoute[] => {
  const routeMap = new Map<string, AppRoute>();
  roles.forEach(role => {
    getRoutesForRole(role).forEach(route => {
      if (route.path && !routeMap.has(route.path)) {
        routeMap.set(route.path, route);
      }
    });
  });
  return Array.from(routeMap.values());
};

const resolveDefaultLayout = (roles: UserRole[]) => {
  const priority: UserRole[] = ["ORG_OWNER", "ORG_ADMIN", "HRM_ADMIN", "HRM_MANAGER", "HRM_EMPLOYEE", "PMO_ADMIN", "PMO_MANAGER", "PMO_USER", "CRM_ADMIN", "CRM_MANAGER", "CRM_USER", "EMPLOYEE", "NONE"];
  for (const r of priority) {
    if (roles.includes(r)) return ROLE_DEFAULT_LAYOUT[r];
  }
  return EmployeeLayout;
};

const resolveLayoutByPath = (
  pathname: string,
  urlOrgSlug: string,
  roles: UserRole[]
): React.ComponentType<{ children: React.ReactNode }> => {
  if (!urlOrgSlug) return resolveDefaultLayout(roles);
  
  for (const { prefix, layout } of PATH_PREFIX_LAYOUT_MAP) {
    if (pathname.startsWith(`/${urlOrgSlug}${prefix}`)) return layout;
  }
  return resolveDefaultLayout(roles);
};

// ─── Route Wrappers ───────────────────────────────────────────────────────────
const PublicRouteWrapper = () => useRoutes(PUBLIC_ROUTES);

const PrivateRouteWrapper = ({ roles }: { roles: UserRole[] }) => {
  const routes = resolveRoutesByRoles(roles);
  const routesWithFallback = [
    ...routes,
    { path: "*", element: <Navigate to="/unauthorized" replace /> }
  ];
  return useRoutes(routesWithFallback);
};

// ─── Main Pages Component ─────────────────────────────────────────────────────
const Pages = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const roles = useAppSelector(selectRoles) ?? [];
  const authUser = useAppSelector(selectAuthUser);
  const location = useLocation();
  const showWelcome = useAppSelector(selectShowWelcome);
  const [showUnauthorized, setShowUnauthorized] = useState(false);

  const urlOrgSlug = location.pathname.split("/").filter(Boolean)[0];
  const isPublicPath = PUBLIC_PATHS_LIST.includes(location.pathname);
  const isSystemSlug = SYSTEM_SLUGS.includes(urlOrgSlug);
  const isOnboardingRoute = location.pathname.includes("onboarding-success");
  const isOrgScopedRoute = !isPublicPath && !isSystemSlug && !isOnboardingRoute;
  const hasValidSlug = Boolean(urlOrgSlug);

  const orgMismatch =
    isAuthenticated && authUser?.orgSlug && hasValidSlug &&
    isOrgScopedRoute && authUser.orgSlug !== urlOrgSlug;

  const isRehydrated = useAppSelector(
    (state: RootState) => (state.auth as any)._persist?.rehydrated ?? false
  );

  useEffect(() => { setShowUnauthorized(false); }, [location.pathname]);

  useEffect(() => {
    if (!orgMismatch) return;
    const timer = setTimeout(() => setShowUnauthorized(true), 2000);
    return () => clearTimeout(timer);
  }, [orgMismatch]);

  if (!isRehydrated) return <FullPageLoader />;

  if (showWelcome) {
    return (
      <div className="fixed inset-0 z-50 bg-primary">
        <OnboardingSuccess />
      </div>
    );
  }

  if (location.pathname === "/unauthorized") return <Unauthorized />;

  if (location.pathname === "/" || (!urlOrgSlug && !isPublicPath)) {
    if (isAuthenticated && authUser?.orgSlug) {
      const defaultRoute = getDefaultRouteByRole(roles, authUser.orgSlug);
      return <Navigate to={defaultRoute} replace />;
    }
    const returnUrl = location.pathname !== "/" ? `?returnUrl=${encodeURIComponent(location.pathname + location.search + location.hash)}` : "";
    return <Navigate to={`/login${returnUrl}`} replace />;
  }

  if (isAuthenticated && authUser?.orgSlug && isPublicPath) {
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const safeReturnUrl = returnUrl?.startsWith("/") && !returnUrl.startsWith("//") 
      ? returnUrl 
      : getDefaultRouteByRole(roles, authUser.orgSlug);
    return <Navigate to={safeReturnUrl} replace />;
  }

  if (orgMismatch) {
    const returnUrl = encodeURIComponent(location.pathname + location.search + location.hash);
    return !showUnauthorized ? <FullPageLoader /> : <Navigate to={`/unauthorized?returnUrl=${returnUrl}`} replace />;
  }

  if (!isAuthenticated) {
    if (!isPublicPath && location.pathname !== "/") {
      return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;
    }
    const NO_LAYOUT_ROUTES = ["/organization-account-setup", "/verify-otp"];
    return NO_LAYOUT_ROUTES.includes(location.pathname) ? (
      <PublicRouteWrapper key={location.pathname} />
    ) : (
      <NonAuthLayout>
        <PublicRouteWrapper key={location.pathname} />
      </NonAuthLayout>
    );
  }

  if (!authUser?.isOrgOnboarded) {
    const isAllowed =
      location.pathname === "/organization-account-setup" ||
      location.pathname === "/employee-management" ||
      location.pathname.includes("onboarding-success");
    if (!isAllowed) return <Navigate to="/organization-account-setup" replace />;
    return (
      <AccountSetupLayout>
        {isPublicPath ? (
          <PublicRouteWrapper key={location.pathname} />
        ) : (
          <PrivateRouteWrapper roles={roles} key={location.pathname} />
        )}
      </AccountSetupLayout>
    );
  }

  if (!roles.length) return <Navigate to="/login" replace />;

  const Layout = resolveLayoutByPath(location.pathname, urlOrgSlug, roles);

  return (
    <NotificationSSEProvider>
      <Layout>
        <PrivateRouteWrapper roles={roles} />
      </Layout>
    </NotificationSSEProvider>
  );
};

export default Pages;